// ==========================================
// BUYERCHATS.JSX
// Estate Experts - Dynamic Buyer Chat
// Features: dynamic conversation selection, search, polling,
// text/file messages, timestamps, auto-scroll, empty/loading states.
// Uses the existing Buyer API endpoints and CSS class names.
// ==========================================

import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import {
  FaSearch,
  FaChevronDown,
  FaPaperPlane,
  FaFile,
  FaPaperclip,
  FaTimes,
} from "react-icons/fa";
import axios from "axios";
import "../../../assets/css/Buyer/dashboard/buyerchats/buyerchats.css";
import BuyerMasterLayout from "../../../layouts/BuyerMasterLayout";
import AppContext from "context/AppContext";

const POLL_INTERVAL = 5000;

const getConversationMessages = (conversation) => {
  const chats = conversation?.exchats;
  if (Array.isArray(chats)) return chats;
  if (chats && typeof chats === "object") return Object.values(chats);
  return [];
};

const formatMessageTime = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function BuyerChats() {
  const { baseUrl, baseUrlForFiles, authToken } = useContext(AppContext);

  const [selectedChat, setSelectedChat] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeChatRef = useRef(null);
  const scrollOnNewMessage = useRef(false);

  useEffect(() => {
    activeChatRef.current = selectedChat;
  }, [selectedChat]);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${authToken}`,
  }), [authToken]);

  const fetchConversation = useCallback(async (initial = false) => {
    const buyerId = localStorage.getItem("buyerId");
    if (!buyerId || !authToken) {
      setConversations([]);
      setSelectedChat(null);
      setLoading(false);
      return;
    }

    if (initial) setLoading(true);

    const formData = new FormData();
    formData.append("exbuyer_id", buyerId);

    try {
      const response = await axios.post(
        `${baseUrl}/buyer/conversation/fetchBuyerConversations`,
        formData,
        { headers: getAuthHeaders() }
      );

      if (response.data?.success) {
        const chats = Array.isArray(response.data.conversations)
          ? response.data.conversations
          : [];
        setConversations(chats);

        setSelectedChat((previous) => {
          if (previous && chats.some((chat) => String(chat.id) === String(previous))) {
            return previous;
          }
          return chats.length ? chats[0].id : null;
        });
      } else if (initial) {
        setError(response.data?.message || "Unable to load conversations.");
      }
    } catch (err) {
      console.error("Buyer conversations error:", err);
      if (initial) setError(err.response?.data?.message || "Unable to load conversations.");
    } finally {
      if (initial) setLoading(false);
    }
  }, [authToken, baseUrl, getAuthHeaders]);

  const fetchConversationChat = useCallback(async (conversationId, options = {}) => {
    if (!conversationId || !authToken) {
      setMessages([]);
      return;
    }

    if (options.showLoader) setChatLoading(true);

    const formData = new FormData();
    formData.append("exconversation_id", conversationId);

    try {
      const response = await axios.post(
        `${baseUrl}/buyer/conversation/fetchMessages`,
        formData,
        { headers: getAuthHeaders() }
      );

      if (String(activeChatRef.current) !== String(conversationId)) return;

      if (response.data?.success) {
        const incoming = Array.isArray(response.data.messages)
          ? response.data.messages
          : response.data.messages && typeof response.data.messages === "object"
            ? Object.values(response.data.messages)
            : [];

        setMessages((previous) => {
          const previousLastId = previous[previous.length - 1]?.id;
          const incomingLastId = incoming[incoming.length - 1]?.id;
          if (previousLastId !== incomingLastId && incoming.length > previous.length) {
            scrollOnNewMessage.current = true;
          }
          return incoming;
        });
      } else if (options.showLoader) {
        setError(response.data?.message || "Unable to load messages.");
      }
    } catch (err) {
      console.error("Buyer messages error:", err);
      if (options.showLoader) {
        setError(err.response?.data?.message || "Unable to load messages.");
      }
    } finally {
      if (options.showLoader) setChatLoading(false);
    }
  }, [authToken, baseUrl, getAuthHeaders]);

  useEffect(() => {
    if (authToken) fetchConversation(true);
    else setLoading(false);
  }, [authToken, fetchConversation]);

  useEffect(() => {
    if (!selectedChat) {
      setMessages([]);
      return;
    }
    setMessageInput("");
    setSelectedFile(null);
    fetchConversationChat(selectedChat, { showLoader: true });
  }, [selectedChat, fetchConversationChat]);

  useEffect(() => {
    if (!authToken) return undefined;

    const interval = setInterval(() => {
      fetchConversation(false);
      if (activeChatRef.current) {
        fetchConversationChat(activeChatRef.current);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [authToken, fetchConversation, fetchConversationChat]);

  useEffect(() => {
    if (scrollOnNewMessage.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      scrollOnNewMessage.current = false;
    }
  }, [messages]);

  useEffect(() => {
    if (selectedChat && messages.length) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [selectedChat]);

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const handleScroll = () => {
    const area = messagesAreaRef.current;
    if (!area) return;
    const nearBottom = area.scrollHeight - area.scrollTop - area.clientHeight < 100;
    setShowScrollButton(!nearBottom);
  };

  const currentChat = conversations.find(
    (chat) => String(chat.id) === String(selectedChat)
  );

  const chatUser = currentChat?.type === "hs"
    ? { firstName: "Admin" }
    : currentChat?.exseller;

  const filteredConversations = conversations.filter((conversation) => {
    const user = conversation?.type === "hs"
      ? { firstName: "Admin" }
      : conversation?.exseller;
    const name = user?.firstName || user?.name || "";
    const history = getConversationMessages(conversation);
    const last = history[history.length - 1];
    const preview = last?.massage || last?.message || "";
    const query = searchQuery.trim().toLowerCase();
    return !query || name.toLowerCase().includes(query) || String(preview).toLowerCase().includes(query);
  });

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const sendMessage = async () => {
    if (!selectedChat || sending || (!messageInput.trim() && !selectedFile)) return;

    setSending(true);
    setError("");
    const formData = new FormData();
    formData.append("exconversation_id", selectedChat);
    formData.append("message", messageInput.trim());
    if (selectedFile) formData.append("file", selectedFile);

    try {
      const response = await axios.post(
        `${baseUrl}/buyer/conversation/sendMessage`,
        formData,
        { headers: getAuthHeaders() }
      );

      if (response.data?.success) {
        setMessageInput("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        await fetchConversationChat(selectedChat);
        await fetchConversation(false);
        scrollOnNewMessage.current = true;
      } else {
        setError(response.data?.message || "Message could not be sent.");
      }
    } catch (err) {
      console.error("Send buyer message error:", err);
      setError(err.response?.data?.message || "Message could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const avatarUrl = (user) => user?.profileImage
    ? `${baseUrlForFiles}/${user.profileImage}`
    : "/default-avatar.png";

  if (loading) {
    return (
      <BuyerMasterLayout pageTitle="Chats">
        <div className="buyerchat-loading">
          <div className="buyerchat-loader">
            <div className="buyerchat-loader-spinner"></div>
          </div>
          <p className="buyerchat-loading-text">Loading your conversations...</p>
        </div>
      </BuyerMasterLayout>
    );
  }

  return (
    <BuyerMasterLayout pageTitle="Chats">
      <div className="buyerchat-container">
        <div className="buyerchat-main">
          {/* LEFT: searchable conversation history */}
          <aside className="buyerchat-sidebar">
            <div className="buyerchat-search-section">
              <div className="buyerchat-search-box">
                <FaSearch className="buyerchat-search-icon" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="buyerchat-search-input"
                />
              </div>
            </div>

            <div className="buyerchat-chat-list">
              {filteredConversations.length === 0 ? (
                <p className="buyerchat-empty">No conversations found.</p>
              ) : filteredConversations.map((conversation) => {
                const user = conversation?.type === "hs"
                  ? { firstName: "Admin" }
                  : conversation?.exseller;
                const history = getConversationMessages(conversation);
                const last = history[history.length - 1];

                return (
                  <div
                    key={conversation.id}
                    className={`buyerchat-chat-item ${
                      String(selectedChat) === String(conversation.id) ? "active" : ""
                    }`}
                    onClick={() => setSelectedChat(conversation.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedChat(conversation.id);
                      }
                    }}
                  >
                    <div className="buyerchat-chat-avatar-wrapper">
                      <img
                        src={avatarUrl(user)}
                        alt={user?.firstName || user?.name || "Chat user"}
                        className="buyerchat-chat-avatar"
                        onError={(event) => { event.currentTarget.src = "/default-avatar.png"; }}
                      />
                    </div>
                    <div className="buyerchat-chat-info">
                      <div className="buyerchat-chat-header-row">
                        <h4 className="buyerchat-agent-name">
                          {user?.firstName || user?.name || "Seller"} {user?.lastName || user?.name || "Seller"}
                        </h4>
                        <span className="buyerchat-message-time">
                          {formatMessageTime(last?.created_at)}
                        </span>
                      </div>
                      <div className="buyerchat-chat-meta">
                        <p className="buyerchat-last-message">
                          {last?.massage || last?.message || "Start a conversation"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* RIGHT: selected conversation */}
          <section className="buyerchat-conversation-area">
            {currentChat ? (
              <>
                <div className="buyerchat-conversation-header">
                  <div className="buyerchat-agent-info">
                    <img
                      src={avatarUrl(chatUser)}
                      alt={chatUser?.firstName || chatUser?.name || "Chat user"}
                      className="buyerchat-chat-avatar"
                      onError={(event) => { event.currentTarget.src = "/default-avatar.png"; }}
                    />
                    <div className="buyerchat-agent-details">
                      <h3 className="buyerchat-agent-name-header">
                        {chatUser?.firstName || chatUser?.name || "Seller"} {chatUser?.lastName || chatUser?.name || "Seller"}
                      </h3>
                      <span className="buyerchat-agent-status">
                        {currentChat?.isOnline ? "Online" : currentChat?.lastSeen || "Conversation"}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className="buyerchat-messages-area"
                  ref={messagesAreaRef}
                  onScroll={handleScroll}
                >
                  {chatLoading ? (
                    <p className="buyerchat-empty">Loading messages...</p>
                  ) : messages.length === 0 ? (
                    <p className="buyerchat-empty">No messages yet. Start the conversation.</p>
                  ) : messages.map((message) => {
                    const mine = message?.sender === "exbuyer";
                    const fileName = typeof message?.file === "string"
                      ? message.file.split("/").pop()
                      : message?.file?.name || message?.file_name;
                    return (
                      <div
                        key={message.id}
                        className={`buyerchat-message ${
                          mine ? "buyerchat-buyer-message" : "buyerchat-agent-message"
                        }`}
                      >
                        <div className="buyerchat-message-bubble">
                          {(message?.massage || message?.message) && (
                            <p className="buyerchat-message-text">
                              {message.massage || message.message}
                            </p>
                          )}
                          {(message?.type === "file" || message?.file) && (
                            <div className="buyerchat-file-message">
                              <FaFile />
                              <span>{fileName || "Attachment"}</span>
                            </div>
                          )}
                          <span className="buyerchat-message-time">
                            {formatMessageTime(message?.created_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {showScrollButton && (
                  <button
                    type="button"
                    className="buyerchat-scroll-to-bottom-btn"
                    onClick={() => scrollToBottom("smooth")}
                    title="Scroll to bottom"
                  >
                    <FaChevronDown />
                  </button>
                )}

                <div className="buyerchat-message-input-area">
                  <input
                    type="text"
                    placeholder="Write a message..."
                    value={messageInput}
                    onChange={(event) => setMessageInput(event.target.value)}
                    onKeyDown={handleKeyDown}
                    className="buyerchat-message-input"
                    disabled={sending}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  {/* <button
                    type="button"
                    className="buyerchat-attachment-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach a file"
                  >
                    <FaPaperclip />
                  </button> */}
                  <button
                    type="button"
                    className="buyerchat-send-btn"
                    disabled={sending || (!messageInput.trim() && !selectedFile)}
                    onClick={sendMessage}
                  >
                    <FaPaperPlane />
                    {sending ? "Sending..." : "Send"}
                  </button>
                  {selectedFile && (
                    <div className="buyerchat-selected-file">
                      <span>{selectedFile.name}</span>
                      <button type="button" onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }} aria-label="Remove attachment">
                        <FaTimes />
                      </button>
                    </div>
                  )}
                </div>
                {error && <p className="buyerchat-error">{error}</p>}
              </>
            ) : (
              <div className="buyerchat-empty-state">
                <h3>Select a conversation</h3>
                <p>Choose a chat from the left to view messages.</p>
                {error && <p className="buyerchat-error">{error}</p>}
              </div>
            )}
          </section>
        </div>
      </div>
    </BuyerMasterLayout>
  );
}

export default BuyerChats;
